import { readFileSync } from 'node:fs';
import { randomUUID } from 'node:crypto';

import { GpmPayConfigError } from '../../core/errors.js';
import { WEBHOOK_DELIVERY_TIMEOUT_MS } from '../../types/enums.js';
import type { WebhookPayload } from '../../types/webhook.js';
import { VERSION } from '../../version.js';
import {
  EVENT_HEADER,
  SIGNATURE_HEADER,
  signWebhookPayload,
} from '../../webhooks/verify.js';
import { enumArg, intArg, requireArg } from '../args.js';
import { type GlobalFlags, secretFromFlags } from '../context.js';
import { printJson } from '../redact.js';
import { EXIT, fields, money, out, style, symbols } from '../ui.js';

/**
 * `webhook send` is the one command that talks to *your* server, not to GPM
 * Pay. It deliberately does not import `clientFromFlags` — no API token is
 * needed, and keeping that a one-line import invariant is why this lives in
 * its own module.
 */
export interface WebhookSendFlags extends GlobalFlags {
  url?: string | undefined;
  secret?: string | undefined;
  file?: string | undefined;
  amount?: string | undefined;
  content?: string | undefined;
  event?: string | undefined;
  type?: string | undefined;
  skew?: string | undefined;
  badSignature?: boolean | undefined;
}

const DEFAULT_AMOUNT = 50_000;

/**
 * ASCII-folded uppercase, because that is what MB and VCB actually emit. The
 * embedded `SHOP0000001` stands in for whatever reference code *you* mint —
 * GPM Pay neither generates nor matches it.
 */
const DEFAULT_CONTENT = 'CT DEN:0123456789 SHOP0000001 thanh toan don hang';

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\]|.*\.local)$/i;

/** @internal exported for tests */
export function buildSamplePayload(flags: WebhookSendFlags): WebhookPayload {
  const content = flags.content ?? DEFAULT_CONTENT;
  const type = enumArg(flags.type, 'type', ['IN', 'OUT'] as const) ?? 'IN';
  const amount = intArg(flags.amount, 'amount') ?? DEFAULT_AMOUNT;

  return {
    id: randomUUID(),
    gateway: 'MB',
    transactionDate: new Date().toISOString(),
    accountNumber: '0123456789',
    subAccount: null,
    content,
    transferType: type === 'IN' ? 'in' : 'out',
    transferAmount: amount,
    accumulated: null,
    // The BANK's own transfer id — not a GPM Pay code, and not yours.
    referenceCode: `FT${String(Date.now())}`,
    source: 'SIMULATED',
  };
}

/** Flip the last hex nibble so the header stays well-formed and fails on
 * `mismatch` rather than `malformed_header` — those are different bugs. */
function corrupt(signature: string): string {
  const last = signature.slice(-1);
  return signature.slice(0, -1) + (last === '0' ? '1' : '0');
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    throw new GpmPayConfigError(
      `Invalid --url "${url}". Expected an absolute URL, e.g. http://localhost:3000/webhooks/gpmpay.`,
      'invalid_argument',
    );
  }
}

export async function sendWebhook(flags: WebhookSendFlags): Promise<number> {
  const url = requireArg(flags.url, 'url');
  const secret = secretFromFlags(flags);
  const host = hostOf(url);

  // One string, signed and sent. Never re-stringified — re-serializing is the
  // single most common cause of "the signature is wrong for no reason".
  const rawBody =
    flags.file === undefined
      ? JSON.stringify(buildSamplePayload(flags))
      : readFileSync(flags.file, 'utf8');

  if (flags.file !== undefined) {
    try {
      JSON.parse(rawBody);
    } catch {
      throw new GpmPayConfigError(
        `--file ${flags.file} is not valid JSON.`,
        'invalid_argument',
      );
    }
  }

  const skew = intArg(flags.skew, 'skew');
  const timestamp = Math.floor(Date.now() / 1000) - (skew ?? 0);
  const signed = signWebhookPayload({ rawBody, secret, timestamp });
  const signature = flags.badSignature === true ? corrupt(signed) : signed;
  const event = flags.event ?? 'transaction.created';

  const parsed = JSON.parse(rawBody) as Partial<WebhookPayload>;

  if (flags.json !== true) {
    out(`${style.dim('→ POST')} ${style.cyan(url)}`);
    out(
      fields([
        ['Event', event],
        // Not a secret: it rides in a header, and printing it lets you replay
        // the exact request with curl. The signing secret is never printed.
        ['Signature', style.dim(signature)],
        [
          'Body',
          `${String(Buffer.byteLength(rawBody))} bytes` +
            (parsed.transferAmount === undefined
              ? ''
              : `  ·  ${money(parsed.transferAmount)} ${String(parsed.transferType ?? '').toUpperCase()}`),
        ],
      ]),
    );

    if (flags.badSignature === true) {
      out(`${symbols.warn()} ${style.yellow('--bad-signature')} — a correct handler must reject this.`);
    }
    if (skew !== undefined) {
      out(
        `${symbols.warn()} ${style.yellow(`--skew ${String(skew)}s`)} — outside the ±300s window, a correct handler must reject this.`,
      );
    }
    if (!LOCAL_HOST.test(host)) {
      out(
        `${symbols.warn()} ${style.yellow(host)} is not local — this is a forged event. ` +
          'A handler that trusts it will act as if a real payment arrived.',
      );
    }
    out();
  }

  const startedAt = Date.now();
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        [SIGNATURE_HEADER]: signature,
        [EVENT_HEADER]: event,
        'user-agent': `gpmpay-cli/${VERSION}`,
      },
      body: rawBody,
      signal: AbortSignal.timeout(WEBHOOK_DELIVERY_TIMEOUT_MS),
    });
  } catch (error) {
    // A raw fetch rejection is a plain TypeError, not a GpmPayConnectionError,
    // so the top-level mapper would call it exit 1. Classify it here.
    const timedOut = (error as Error).name === 'TimeoutError';
    out(
      `${symbols.fail()} ${style.red(timedOut ? 'timed out' : 'could not connect')} — ${url}`,
    );
    out(
      style.dim(
        timedOut
          ? `  No response within ${String(WEBHOOK_DELIVERY_TIMEOUT_MS)} ms. GPM Pay aborts real deliveries\n` +
              '  at the same budget, so this would be recorded as failed in production.'
          : '  Is your server running, and is the path right?',
      ),
    );
    return EXIT.CONNECTION;
  }

  const durationMs = Date.now() - startedAt;
  const responseBody = await response.text();

  if (flags.json === true) {
    printJson({
      url,
      event,
      signature,
      requestBody: parsed,
      status: response.status,
      ok: response.ok,
      durationMs,
      responseBody,
    });
    return response.ok ? EXIT.OK : EXIT.ERROR;
  }

  const line = `${String(response.status)} ${response.statusText}  ${style.dim(`(${String(durationMs)} ms)`)}`;
  out(response.ok ? `${symbols.ok()} ${line}` : `${symbols.fail()} ${style.red(line)}`);
  if (responseBody !== '') out(style.dim(`  ${responseBody.slice(0, 200)}`));

  if (!response.ok && flags.badSignature !== true && skew === undefined) {
    out();
    out(
      style.dim(
        '  Your handler rejected the delivery. Check that it verifies the RAW body\n' +
          '  bytes — not JSON.stringify(req.body) — and uses the same secret.',
      ),
    );
  }
  if (response.ok) {
    out();
    out(
      style.dim(
        '  source=SIMULATED — if your handler skips simulated traffic, pass --file\n' +
          '  with a body of your own.',
      ),
    );
  }

  return response.ok ? EXIT.OK : EXIT.ERROR;
}
